import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, MapPin, Briefcase, DollarSign, Clock, Building2, ExternalLink, IdCard, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { safeHtml } from "@/lib/sanitize";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import ReportJobButton from "@/components/jobs/ReportJobButton";
import { formatJobPay, payToneForJob } from "@/lib/payDisplay";
import { JobSalaryInsight } from "@/components/jobs/JobSalaryInsight";

/** Public, unauthenticated job detail page.
 *
 * Renders full crawlable HTML including <title>, meta description, canonical,
 * OpenGraph tags, and Google JobPosting JSON-LD. When the job is expired we
 * OMIT the JSON-LD (Google guidance for closed listings) and show the
 * "no longer accepting applications" copy with a link back to /jobs.
 *
 * NEVER reads private fields — the /public/jobs/by-slug/{slug} backend
 * endpoint already whitelists what the SPA can see.
 */
const PUBLIC_ORIGIN = process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_BACKEND_URL;

function truncate(s, n) { s = (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " "); return s.length > n ? s.slice(0, n - 1) + "…" : s; }

function buildJsonLd(job, canonical) {
    // Only emit when the job is live — expired listings must not keep the
    // JobPosting schema active or Google flags it as a stale posting.
    if (job.is_expired) return null;
    const ld = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": job.title,
        "description": job.description || "",
        "datePosted": job.posted_at,
        "hiringOrganization": {
            "@type": "Organization",
            "name": job.company || "Simonara Employer",
            // Only emit `logo` when the employer has actually uploaded one —
            // fabricating a favicon URL gets your Rich Result flagged as spam.
            ...(job.company_logo_url ? { "logo": job.company_logo_url } : {}),
        },
        "employmentType": (job.employment_type || "").toUpperCase().replace(/[- ]/g, "_") || undefined,
        "url": canonical,
        "directApply": Boolean(job.apply_url),
        // Requisition identifier — helps Google + external ATSes de-dupe when
        // the same role gets syndicated to multiple boards.
        ...(job.req_number ? {
            "identifier": {
                "@type": "PropertyValue",
                "name": "Simonara Req",
                "value": job.req_number,
            },
        } : {}),
    };
    if (job.valid_through) ld.validThrough = job.valid_through;
    // Location — Google supports jobLocation (physical) + applicantLocationRequirements (remote).
    if (job.remote_status === "remote" || /^remote/i.test(job.location || "")) {
        ld.jobLocationType = "TELECOMMUTE";
        ld.applicantLocationRequirements = { "@type": "Country", "name": "US" };
    }
    if (job.location && job.remote_status !== "remote") {
        const [city, state] = job.location.split(",").map((s) => (s || "").trim());
        ld.jobLocation = {
            "@type": "Place",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": city || undefined,
                "addressRegion": state || undefined,
                "addressCountry": "US",
            },
        };
    }
    // Salary — emit ONLY if a real number is present. Fabricating ranges gets
    // your Rich Result flagged as spam.
    const hasSalary = (job.salary_min && job.salary_max)
        || (typeof job.salary === "number" && job.salary > 0);
    if (hasSalary) {
        ld.baseSalary = {
            "@type": "MonetaryAmount",
            "currency": job.salary_currency || "USD",
            "value": {
                "@type": "QuantitativeValue",
                "minValue": job.salary_min || job.salary,
                "maxValue": job.salary_max || job.salary,
                "unitText": (job.salary_period || "YEAR").toUpperCase(),
            },
        };
    }
    return JSON.stringify(ld);
}

export default function PublicJobPage() {
    const params = useParams();
    const { user } = useAuth();
    // Route may be `:slug` (new) OR `:id` (legacy). Backend handles either.
    const slug = params.slug || params.id;
    const [job, setJob] = useState(null);
    const [state, setState] = useState("loading"); // loading | ok | notfound | error

    useEffect(() => {
        let cancelled = false;
        setState("loading");
        api.get(`/public/jobs/by-slug/${slug}`)
            .then((r) => {
                if (cancelled) return;
                setJob(r.data);
                setState("ok");
                trackEvent("job_page_viewed", {
                    job_id: r.data.id,
                    job_slug: r.data.slug || slug,
                    authenticated: !!user,
                    user_type: user?.role || "guest",
                });
            })
            .catch((err) => { if (!cancelled) setState(err?.response?.status === 404 ? "notfound" : "error"); });
        return () => { cancelled = true; };
    }, [slug, user]);

    if (state === "loading") {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={22} className="animate-spin text-violet-500" /></div>;
    }

    if (state === "notfound") {
        return (
            <div className="max-w-2xl mx-auto p-8 text-center" data-testid="public-job-notfound">
                <Helmet>
                    <title>Job not found — Simonara</title>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
                <h1 className="text-2xl font-heading font-bold">Job not found</h1>
                <p className="mt-2 text-muted-foreground">This posting is no longer available. Browse other openings on Simonara.</p>
                <Link to="/jobs" className="mt-4 inline-block px-4 py-2 rounded-md bg-violet-500 text-white font-medium">See open jobs</Link>
            </div>
        );
    }

    if (state === "error" || !job) {
        return <div className="max-w-2xl mx-auto p-8 text-center text-muted-foreground">Couldn&apos;t load this job. Try again in a moment.</div>;
    }

    const canonical = job.canonical_url || `${PUBLIC_ORIGIN}/jobs/${slug}`;
    const jsonLd = buildJsonLd(job, canonical);
    const title = `${job.title}${job.location ? " · " + job.location : ""} — Simonara`;
    const desc = truncate(job.description, 160) || `Apply for ${job.title} at ${job.company || "a vetted Simonara employer"}.`;

    return (
        <article className="max-w-3xl mx-auto p-6 lg:p-10" data-testid="public-job-page">
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={desc} />
                <link rel="canonical" href={canonical} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={desc} />
                <meta property="og:url" content={canonical} />
                <meta property="og:site_name" content="Simonara Talent" />
                <meta name="twitter:card" content="summary_large_image" />
                {job.is_expired && <meta name="robots" content="noindex, follow" />}
                {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
            </Helmet>

            {job.is_expired && (
                <div className="mb-6 p-4 rounded-md border border-amber-500/40 bg-amber-500/5" data-testid="public-job-expired">
                    <div className="font-medium text-amber-700 dark:text-amber-400">This position is no longer accepting applications.</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        The role may have been filled or the posting has expired. Browse similar active openings on the
                        {" "}<Link to="/jobs" className="underline text-violet-600">jobs board</Link>.
                    </p>
                </div>
            )}

            <header className="mb-6">
                {job.req_number && (
                    <div
                        className="text-[10px] uppercase tracking-wider font-mono font-semibold text-muted-foreground mb-2"
                        data-testid="public-job-req"
                    >
                        Req · {job.req_number}
                    </div>
                )}
                <h1 className="text-3xl lg:text-4xl font-heading font-bold" data-testid="public-job-title">{job.title}</h1>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
                    {job.company && <span className="inline-flex items-center gap-1" data-testid="public-job-company"><Building2 size={13} /> {job.company}</span>}
                    {job.location && <span className="inline-flex items-center gap-1" data-testid="public-job-location"><MapPin size={13} /> {job.location}</span>}
                    {job.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {job.employment_type}</span>}
                    {/* iter 149.5 — unified applicant-side pay pill.
                        Prefers pay_mode over legacy salary string; renders
                        an amber pill for DOE, emerald for concrete numbers,
                        and nothing at all when the job has no pay signal. */}
                    {(() => {
                        const label = formatJobPay(job);
                        const tone = payToneForJob(job);
                        if (!label || tone === "none") return null;
                        return (
                            <span
                                data-testid="public-job-salary"
                                data-pay-tone={tone}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${tone === "doe"
                                    ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}`}
                            >
                                <DollarSign size={13} /> {label}
                            </span>
                        );
                    })()}
                    {job.posted_at && <span className="inline-flex items-center gap-1" data-testid="public-job-posted"><Clock size={13} /> Posted {job.posted_at.slice(0, 10)}</span>}
                </div>
                {!job.is_expired && <JobSalaryInsight slug={slug} />}
                <div className="mt-2"><ReportJobButton jobId={job.id} /></div>
            </header>

            <section className="prose prose-slate dark:prose-invert max-w-none" data-testid="public-job-description">
                {job.description && <div dangerouslySetInnerHTML={{ __html: safeHtml(job.description) }} />}
                {job.responsibilities && (
                    <>
                        <h2>Responsibilities</h2>
                        <div dangerouslySetInnerHTML={{ __html: safeHtml(job.responsibilities) }} />
                    </>
                )}
                {job.requirements && (
                    <>
                        <h2>Requirements</h2>
                        <div dangerouslySetInnerHTML={{ __html: safeHtml(job.requirements) }} />
                    </>
                )}
                {job.benefits && (
                    <>
                        <h2>Benefits</h2>
                        <div dangerouslySetInnerHTML={{ __html: safeHtml(job.benefits) }} />
                    </>
                )}
            </section>

            {!job.is_expired && (() => {
                // Passport-First Apply — turn every job page into a passport
                // conversion asset. If the user is signed out (or signed in
                // without a professional passport yet), the primary CTA now
                // routes them into the register / passport-onboarding flow
                // with a `next=` deep-link back to this job so we auto-open
                // the internal apply screen the moment their passport is live.
                const applyIntent = `/jobs/${slug}/apply`;
                const nextParam = encodeURIComponent(applyIntent);
                const passportOnboardHref = user
                    ? `/talent/passport?next=${nextParam}`     // signed-in pro → resume passport
                    : `/register?intent=apply&next=${nextParam}`;
                const fireApplyStart = () => {
                    trackEvent("job_application_started", {
                        job_id: job.id,
                        job_slug: job.slug || slug,
                        authenticated: !!user,
                        user_type: user?.role || "guest",
                    });
                };
                return (
                    <div className="mt-8" data-testid="public-job-apply-block">
                        <div className="flex flex-wrap items-center gap-3">
                            {job.apply_url ? (
                                // Employer supplied an external ATS link — respect it. Still fires
                                // the analytics event so we can measure funnel drop.
                                <a
                                    href={job.apply_url}
                                    rel="noopener noreferrer nofollow"
                                    target="_blank"
                                    onClick={fireApplyStart}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-medium"
                                    data-testid="public-job-apply"
                                >
                                    Apply now <ExternalLink size={14} />
                                </a>
                            ) : user && user.role === "professional" ? (
                                // Signed-in professional — go straight to internal apply flow.
                                <Link
                                    to={applyIntent}
                                    onClick={fireApplyStart}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-medium"
                                    data-testid="public-job-apply-internal"
                                >
                                    Apply on Simonara <ArrowRight size={14} />
                                </Link>
                            ) : (
                                // Guest or non-professional signed-in → passport-first path.
                                <Link
                                    to={passportOnboardHref}
                                    onClick={fireApplyStart}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                                    data-testid="public-job-apply-passport-first"
                                >
                                    <IdCard size={16} /> Apply with Talent Passport™ <ArrowRight size={14} />
                                </Link>
                            )}
                            {!job.apply_url && (!user || user.role !== "professional") && (
                                <Link
                                    to={`/login?next=${nextParam}`}
                                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
                                    data-testid="public-job-apply-signin"
                                >
                                    Already have a passport? Sign in
                                </Link>
                            )}
                        </div>
                        {!job.apply_url && (!user || user.role !== "professional") && (
                            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
                                Your Talent Passport™ is the single profile that unlocks this role — plus every future match. Build it once, apply anywhere on Simonara.
                            </p>
                        )}
                    </div>
                );
            })()}
        </article>
    );
}
